import { useState, useEffect, useCallback } from 'react';
import { hospitalApi } from '../../hospitalApi';

const C    = '#1b6ca8';
const DARK = '#0f4c75';
const LIGHT = '#e8f4fd';

const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const today = () => new Date().toISOString().split('T')[0];
const isExpired = (d) => d && new Date(d) < new Date(new Date().toDateString());

const EMPTY_FORM = { patientId: '', doctorId: '', billDate: today(), discount: '0', taxPct: '0', notes: '' };
const EMPTY_LINE = { inventoryItemId: '', medicineName: '', quantity: '1', unit: '', rate: '0' };

const inp = { className: 'form-control' };

function F({ label, children, col }) {
  return (
    <div className="form-group" style={col ? { gridColumn: col } : {}}>
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

// ── Print helper ─────────────────────────────────────────────────────────────
function printMedicineBill(bill, items, info) {
  const h = info || {};
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Medicine Bill ${bill.bill_no}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 13px; color: #222; padding: 32px; max-width: 700px; margin: auto; }
  .hname { font-size: 22px; font-weight: 800; color: #1b6ca8; }
  .hsub  { font-size: 12px; color: #555; margin-top: 3px; }
  .div1  { border: none; border-top: 2px solid #1b6ca8; margin: 14px 0; }
  .div2  { border: none; border-top: 1px solid #ddd; margin: 10px 0; }
  .title { font-size: 16px; font-weight: 700; text-align: center; letter-spacing: 2px; margin: 10px 0; color: #1b6ca8; }
  .meta  { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 20px; margin: 10px 0; }
  .lbl   { font-size: 11px; color: #777; }
  .val   { font-weight: 600; }
  table  { width: 100%; border-collapse: collapse; margin: 12px 0; }
  th     { background: #e8f4fd; color: #1b6ca8; font-size: 12px; font-weight: 700; padding: 7px 10px; text-align: left; }
  td     { padding: 7px 10px; border-bottom: 1px solid #f0f0f0; font-size: 13px; }
  .ar    { text-align: right; }
  .ac    { text-align: center; }
  .tot   { font-weight: 800; font-size: 15px; color: #27ae60; background: #e6f4ea; }
  .tot td { padding: 9px 10px; }
  .badge { display:inline-block; padding:4px 12px; border-radius:6px; font-weight:700; font-size:12px; }
  .paid  { background:#e6f4ea; color:#27ae60; }
  .pend  { background:#fce8e6; color:#c5221f; }
  .fn    { font-size:11px; color:#999; text-align:center; margin-top:20px; }
  @media print { button { display:none; } }
</style></head><body>
<div style="display:flex;justify-content:space-between;align-items:flex-start">
  <div>
    <div class="hname">${h.hospital_name || 'Hospital'}</div>
    <div class="hsub">${[h.address, h.phone, h.email].filter(Boolean).join(' | ')}</div>
    ${h.license_no ? `<div class="hsub">License: ${h.license_no}${h.gst_no ? ' | GST: ' + h.gst_no : ''}</div>` : ''}
  </div>
  <div style="text-align:right">
    <div class="badge ${bill.status === 'paid' ? 'paid' : 'pend'}">${(bill.status || 'PENDING').toUpperCase()}</div>
  </div>
</div>
${h.bill_header ? `<div style="font-size:12px;color:#555;margin-top:6px">${h.bill_header}</div>` : ''}
<hr class="div1">
<div class="title">MEDICINE BILL</div>
<div class="meta">
  <div><div class="lbl">Bill No</div><div class="val">${bill.bill_no || '—'}</div></div>
  <div><div class="lbl">Date</div><div class="val">${bill.bill_date || '—'}</div></div>
  <div><div class="lbl">Patient</div><div class="val">${bill.patient_name || '—'}</div></div>
  <div><div class="lbl">Doctor</div><div class="val">${bill.doctor_name ? 'Dr. ' + bill.doctor_name : '—'}</div></div>
</div>
<hr class="div2">
<table>
  <thead><tr>
    <th>#</th><th>Medicine</th><th class="ac">Qty</th><th>Unit</th><th class="ar">Rate</th><th class="ar">Amount</th>
  </tr></thead>
  <tbody>
    ${items.map((it, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${it.medicine_name || it.medicineName || '—'}</td>
        <td class="ac">${Number(it.quantity)}</td>
        <td>${it.unit || '—'}</td>
        <td class="ar">${fmtRs(it.rate)}</td>
        <td class="ar" style="font-weight:600">${fmtRs(it.total)}</td>
      </tr>`).join('')}
  </tbody>
  <tfoot>
    <tr style="background:#fafafa"><td colspan="5" style="font-weight:600;text-align:right">Subtotal</td><td class="ar" style="font-weight:600">${fmtRs(bill.subtotal)}</td></tr>
    ${Number(bill.discount) > 0 ? `<tr><td colspan="5" style="text-align:right">Discount</td><td class="ar" style="color:#c0392b">− ${fmtRs(bill.discount)}</td></tr>` : ''}
    ${Number(bill.tax_pct) > 0 ? `<tr><td colspan="5" style="text-align:right">Tax (${bill.tax_pct}%)</td><td class="ar">${fmtRs(bill.tax_amount)}</td></tr>` : ''}
    <tr class="tot"><td colspan="5" style="text-align:right">TOTAL</td><td class="ar">${fmtRs(bill.total)}</td></tr>
  </tfoot>
</table>
${bill.status === 'paid' ? `<div style="margin:10px 0;font-size:13px"><strong>Payment:</strong> ${fmtRs(bill.paid_amount)} via ${(bill.payment_mode || '').toUpperCase()}</div>` : ''}
${bill.notes ? `<div style="margin-top:10px;font-size:12px;color:#555"><strong>Notes:</strong> ${bill.notes}</div>` : ''}
${h.bill_footer ? `<div style="font-size:12px;color:#555;margin-top:10px;border-top:1px solid #eee;padding-top:10px">${h.bill_footer}</div>` : ''}
<div class="fn">This is a computer-generated bill and does not require a signature.</div>
<div style="text-align:center;margin-top:10px">
  <button onclick="window.print()" style="padding:8px 24px;background:#1b6ca8;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer">🖨 Print</button>
</div>
</body></html>`;
  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 400);
}

// ── Bill card (list view) ─────────────────────────────────────────────────────
function BillCard({ bill, onPay, onDelete, role, hospitalInfo }) {
  const [expanded, setExpanded] = useState(false);
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showPay, setShowPay]   = useState(false);
  const [payForm, setPayForm]   = useState({ paidAmount: bill.total || '', paymentMode: 'cash' });
  const [paying, setPaying]     = useState(false);

  const expand = async () => {
    if (!expanded && items.length === 0) {
      setLoading(true);
      const d = await hospitalApi.getMedicineBill(bill.id);
      if (d && d.items) setItems(d.items);
      setLoading(false);
    }
    setExpanded(e => !e);
  };

  const handlePay = async () => {
    setPaying(true);
    await onPay(bill.id, payForm);
    setPaying(false);
    setShowPay(false);
  };

  return (
    <div style={{
      background: '#fff', borderRadius: 10, boxShadow: '0 1px 6px rgba(0,0,0,.08)',
      marginBottom: 10, overflow: 'hidden',
      borderLeft: `4px solid ${bill.status === 'paid' ? '#27ae60' : '#e67e22'}`,
    }}>
      <div style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
        onClick={expand}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: DARK }}>{bill.patient_name || '—'}</div>
            <div style={{ fontWeight: 800, fontSize: 15, color: '#27ae60' }}>{fmtRs(bill.total)}</div>
          </div>
          <div style={{ fontSize: 11, color: '#5f6368' }}>
            {bill.bill_no} · {bill.bill_date}
            {bill.doctor_name ? ` · Dr. ${bill.doctor_name}` : ''}
            {` · ${bill.item_count || 0} medicine${bill.item_count !== 1 ? 's' : ''}`}
          </div>
        </div>
        <span style={{
          background: bill.status === 'paid' ? '#e6f4ea' : '#fef3c7',
          color: bill.status === 'paid' ? '#27ae60' : '#e67e22',
          borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700,
        }}>{bill.status}</span>
        <span style={{ color: '#9aa0a6', fontSize: 14 }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={{ padding: '0 16px 14px', borderTop: '1px solid #f1f3f4' }}>
          {loading ? (
            <div style={{ padding: 12, color: '#9aa0a6', fontSize: 13 }}>Loading…</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 10 }}>
              <thead>
                <tr style={{ background: LIGHT }}>
                  {['Medicine', 'Qty', 'Unit', 'Rate', 'Amount'].map(h => (
                    <th key={h} style={{ padding: '6px 10px', textAlign: h === 'Amount' || h === 'Rate' ? 'right' : 'left', color: DARK, fontWeight: 700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #eef0f2' }}>
                    <td style={{ padding: '6px 10px', color: DARK, fontWeight: 600 }}>{it.medicine_name}</td>
                    <td style={{ padding: '6px 10px', color: '#5f6368' }}>{Number(it.quantity)}</td>
                    <td style={{ padding: '6px 10px', color: '#5f6368' }}>{it.unit || '—'}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', color: '#5f6368' }}>{fmtRs(it.rate)}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700, color: DARK }}>{fmtRs(it.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {Number(bill.discount) > 0 && (
                  <tr style={{ background: '#fafafa' }}>
                    <td colSpan={4} style={{ padding: '6px 10px', textAlign: 'right', color: '#5f6368' }}>Discount</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', color: '#c0392b', fontWeight: 600 }}>− {fmtRs(bill.discount)}</td>
                  </tr>
                )}
                {Number(bill.tax_pct) > 0 && (
                  <tr style={{ background: '#fafafa' }}>
                    <td colSpan={4} style={{ padding: '6px 10px', textAlign: 'right', color: '#5f6368' }}>Tax ({bill.tax_pct}%)</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', color: '#5f6368', fontWeight: 600 }}>{fmtRs(bill.tax_amount)}</td>
                  </tr>
                )}
                <tr style={{ background: '#e6f4ea' }}>
                  <td colSpan={4} style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 800, color: DARK }}>TOTAL</td>
                  <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 800, color: '#27ae60', fontSize: 14 }}>{fmtRs(bill.total)}</td>
                </tr>
              </tfoot>
            </table>
          )}
          {bill.notes && <div style={{ fontSize: 12, color: '#5f6368', marginTop: 8 }}>Notes: {bill.notes}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            {bill.status !== 'paid' && (
              <button className="btn btn-primary" style={{ fontSize: 12, padding: '5px 14px' }}
                onClick={() => setShowPay(!showPay)}>💳 Pay Now</button>
            )}
            <button style={{ fontSize: 12, padding: '5px 14px', background: LIGHT, color: DARK,
              border: `1px solid ${C}`, borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
              onClick={() => printMedicineBill(bill, items, hospitalInfo)}>🖨 Print</button>
            {role === 'admin' && (
              <button style={{ fontSize: 12, padding: '5px 14px', background: '#fce8e6',
                color: '#c5221f', border: '1px solid #f5c6c6', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                onClick={() => onDelete(bill.id)}>Delete</button>
            )}
          </div>
          {showPay && (
            <div style={{ marginTop: 12, padding: 12, background: LIGHT, borderRadius: 8 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: 12 }}>Amount Paid</label>
                  <input className="form-control" type="number" min={0}
                    value={payForm.paidAmount}
                    onChange={e => setPayForm(f => ({ ...f, paidAmount: e.target.value }))} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: 12 }}>Payment Mode</label>
                  <select className="form-control" value={payForm.paymentMode}
                    onChange={e => setPayForm(f => ({ ...f, paymentMode: e.target.value }))}>
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="upi">UPI</option>
                    <option value="netbanking">Net Banking</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
                <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={handlePay} disabled={paying}>
                  {paying ? 'Processing…' : '✓ Confirm Payment'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── New bill form ─────────────────────────────────────────────────────────────
function NewBillForm({ patients, doctors, inventory, onSaved, onCancel }) {
  const [form, setForm]         = useState(EMPTY_FORM);
  const [lines, setLines]       = useState([{ ...EMPTY_LINE }]);
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const updateLine = (idx, field, value) =>
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));

  const selectMedicine = (idx, invId) => {
    const item = inventory.find(it => String(it.id) === String(invId));
    if (!item) { updateLine(idx, 'inventoryItemId', ''); return; }
    if (isExpired(item.expiry_date)) {
      setErr(`"${item.name}" is expired (${item.expiry_date}). Cannot add to bill.`);
      return;
    }
    setErr('');
    setLines(prev => prev.map((l, i) => i === idx ? {
      ...l,
      inventoryItemId: String(item.id),
      medicineName: item.name,
      unit: item.unit || '',
      rate: String(item.unit_rate || 0),
    } : l));
  };

  const subtotal = lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.rate) || 0), 0);
  const discount = parseFloat(form.discount) || 0;
  const taxPct   = parseFloat(form.taxPct) || 0;
  const taxAmt   = (subtotal - discount) * taxPct / 100;
  const total    = subtotal - discount + taxAmt;

  const handleSave = async () => {
    if (!form.patientId) { setErr('Patient is required'); return; }
    const validLines = lines.filter(l => l.medicineName.trim() && parseFloat(l.quantity) > 0);
    if (validLines.length === 0) { setErr('Add at least one medicine with quantity'); return; }
    // Block if any linked inventory item is expired
    const expiredLine = validLines.find(l => {
      if (!l.inventoryItemId) return false;
      const item = inventory.find(it => String(it.id) === String(l.inventoryItemId));
      return item && isExpired(item.expiry_date);
    });
    if (expiredLine) {
      const item = inventory.find(it => String(it.id) === String(expiredLine.inventoryItemId));
      setErr(`Cannot bill expired medicine: "${item?.name}" (expired ${item?.expiry_date})`);
      return;
    }
    setSaving(true); setErr('');
    try {
      await hospitalApi.createMedicineBill({
        patientId: parseInt(form.patientId),
        doctorId:  parseInt(form.doctorId) || null,
        billDate:  form.billDate || today(),
        discount,
        taxPct,
        notes: form.notes,
        items: validLines.map(l => ({
          inventoryItemId: parseInt(l.inventoryItemId) || null,
          medicineName: l.medicineName,
          quantity: parseFloat(l.quantity) || 0,
          unit: l.unit,
          rate: parseFloat(l.rate) || 0,
          total: (parseFloat(l.quantity) || 0) * (parseFloat(l.rate) || 0),
        })),
      });
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <div className="card-title">💊 New Medicine Bill</div>
      </div>
      <div className="card-body">
        {err && <div style={{ color: '#c5221f', background: '#fce8e6', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>⚠️ {err}</div>}

        {/* Header fields */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
          <F label="Patient *">
            <select {...inp} value={form.patientId} onChange={e => setF('patientId', e.target.value)}>
              <option value="">Select Patient</option>
              {patients.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </F>
          <F label="Doctor">
            <select {...inp} value={form.doctorId} onChange={e => setF('doctorId', e.target.value)}>
              <option value="">Select Doctor</option>
              {doctors.map(d => <option key={d.id} value={d.id}>Dr. {d.name}</option>)}
            </select>
          </F>
          <F label="Bill Date">
            <input {...inp} type="date" value={form.billDate} onChange={e => setF('billDate', e.target.value)} />
          </F>
        </div>

        {/* Line items */}
        <div style={{ fontWeight: 700, color: DARK, fontSize: 14, marginBottom: 10 }}>Medicine Items</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 10 }}>
            <thead>
              <tr style={{ background: LIGHT }}>
                {['Medicine (from Inventory)', 'Custom Name', 'Qty', 'Unit', 'Rate (₹)', 'Amount', ''].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => {
                const lineTotal = (parseFloat(l.quantity) || 0) * (parseFloat(l.rate) || 0);
                return (
                  <tr key={idx} style={{ borderTop: '1px solid #f1f3f4' }}>
                    <td style={{ padding: '5px 5px', minWidth: 180 }}>
                      <select {...inp} value={l.inventoryItemId} style={{ fontSize: 12 }}
                        onChange={e => selectMedicine(idx, e.target.value)}>
                        <option value="">-- Pick from inventory --</option>
                        {inventory.map(it => (
                          <option key={it.id} value={it.id} disabled={isExpired(it.expiry_date)}>
                            {isExpired(it.expiry_date) ? `⛔ ${it.name} [EXPIRED ${it.expiry_date}]` : `${it.name} (Stock: ${Number(it.current_stock) || 0} ${it.unit || ''})`}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '5px 5px', minWidth: 140 }}>
                      <input {...inp} value={l.medicineName} placeholder="or type name"
                        style={{ fontSize: 12 }}
                        onChange={e => updateLine(idx, 'medicineName', e.target.value)} />
                    </td>
                    <td style={{ padding: '5px 5px', width: 80 }}>
                      <input {...inp} type="number" min={0} step="any" value={l.quantity}
                        style={{ fontSize: 12 }}
                        onChange={e => updateLine(idx, 'quantity', e.target.value)} />
                    </td>
                    <td style={{ padding: '5px 5px', width: 70 }}>
                      <input {...inp} value={l.unit} placeholder="pcs"
                        style={{ fontSize: 12 }}
                        onChange={e => updateLine(idx, 'unit', e.target.value)} />
                    </td>
                    <td style={{ padding: '5px 5px', width: 100 }}>
                      <input {...inp} type="number" min={0} step="any" value={l.rate}
                        style={{ fontSize: 12 }}
                        onChange={e => updateLine(idx, 'rate', e.target.value)} />
                    </td>
                    <td style={{ padding: '5px 10px', fontWeight: 700, color: DARK, whiteSpace: 'nowrap' }}>
                      {fmtRs(lineTotal)}
                    </td>
                    <td style={{ padding: '5px 5px', width: 36 }}>
                      {lines.length > 1 && (
                        <button onClick={() => setLines(prev => prev.filter((_, i) => i !== idx))}
                          style={{ background: '#fce8e6', border: '1px solid #f5c6c6', borderRadius: 4,
                            color: '#c5221f', cursor: 'pointer', fontSize: 14, padding: '2px 7px', fontWeight: 700 }}>×</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <button style={{ fontSize: 12, padding: '5px 14px', background: LIGHT, color: DARK,
          border: `1px solid ${C}`, borderRadius: 6, cursor: 'pointer', fontWeight: 600, marginBottom: 16 }}
          onClick={() => setLines(prev => [...prev, { ...EMPTY_LINE }])}>+ Add Medicine</button>

        {/* Footer fields */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
          <F label="Discount (₹)">
            <input {...inp} type="number" min={0} value={form.discount} onChange={e => setF('discount', e.target.value)} />
          </F>
          <F label="Tax (%)">
            <input {...inp} type="number" min={0} max={100} step={0.5} value={form.taxPct} onChange={e => setF('taxPct', e.target.value)} />
          </F>
          <F label="Notes" col="1/-1">
            <textarea {...inp} rows={2} value={form.notes} onChange={e => setF('notes', e.target.value)} style={{ resize: 'vertical' }} />
          </F>
        </div>

        {/* Total summary */}
        <div style={{ marginTop: 14, padding: 14, background: LIGHT, borderRadius: 8, display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, color: '#5f6368' }}>Subtotal</div>
            <div style={{ fontWeight: 700, color: DARK, fontSize: 15 }}>{fmtRs(subtotal)}</div>
          </div>
          {discount > 0 && (
            <div>
              <div style={{ fontSize: 11, color: '#5f6368' }}>Discount</div>
              <div style={{ fontWeight: 700, color: '#c0392b', fontSize: 15 }}>− {fmtRs(discount)}</div>
            </div>
          )}
          {taxPct > 0 && (
            <div>
              <div style={{ fontSize: 11, color: '#5f6368' }}>Tax ({taxPct}%)</div>
              <div style={{ fontWeight: 700, color: '#e67e22', fontSize: 15 }}>{fmtRs(taxAmt)}</div>
            </div>
          )}
          <div>
            <div style={{ fontSize: 11, color: '#5f6368' }}>Total</div>
            <div style={{ fontWeight: 800, color: '#27ae60', fontSize: 20 }}>{fmtRs(total)}</div>
          </div>
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : '💾 Save Bill'}
          </button>
          <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
            background: '#fff', cursor: 'pointer', fontSize: 13 }}
            onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function HospitalMedicineBill({ role }) {
  const [tab, setTab]             = useState('pending');
  const [bills, setBills]         = useState([]);
  const [patients, setPatients]   = useState([]);
  const [doctors, setDoctors]     = useState([]);
  const [inventory, setInventory] = useState([]);
  const [hospitalInfo, setHospitalInfo] = useState({});
  const [showForm, setShowForm]   = useState(false);

  const load = useCallback(() => {
    hospitalApi.getMedicineBills(tab === 'pending' ? 'pending' : '').then(d => { if (d) setBills(d); });
  }, [tab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    hospitalApi.getPatients().then(d => { if (d) setPatients(d); });
    hospitalApi.getDoctors().then(d => { if (d) setDoctors(d.filter(doc => doc.status === 'active')); });
    hospitalApi.getInventoryItems().then(d => { if (d) setInventory(d); });
    hospitalApi.getInfo().then(d => { if (d) setHospitalInfo(d); });
  }, []);

  const handlePay = async (id, payData) => {
    await hospitalApi.payMedicineBill(id, { paidAmount: parseFloat(payData.paidAmount) || 0, paymentMode: payData.paymentMode });
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this medicine bill? Stock will be restored.')) return;
    await hospitalApi.deleteMedicineBill(id);
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div className="tabs" style={{ marginBottom: 0 }}>
          {[
            { key: 'pending', label: '⏳ Pending' },
            { key: 'all',     label: '📋 All Bills' },
          ].map(t => (
            <button key={t.key} className={`tab${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        {role !== 'viewer' && (
          <button className="btn btn-primary" onClick={() => setShowForm(s => !s)}>
            {showForm ? '✕ Cancel' : '+ New Medicine Bill'}
          </button>
        )}
      </div>

      {showForm && (
        <NewBillForm
          patients={patients}
          doctors={doctors}
          inventory={inventory}
          onSaved={() => { setShowForm(false); load(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {bills.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#9aa0a6', background: '#fff',
          borderRadius: 10, boxShadow: '0 1px 6px rgba(0,0,0,.08)' }}>
          No medicine bills found
        </div>
      ) : (
        bills.map(b => (
          <BillCard key={b.id} bill={b} onPay={handlePay} onDelete={handleDelete}
            role={role} hospitalInfo={hospitalInfo} />
        ))
      )}
    </div>
  );
}
